import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DjiDraftPreview, DjiDroneStatus, DjiFlight } from '../models/dji.model';
import { DjiRequestError, toDjiRequestError } from './dji-api-error';

/** The Hub's ResponseModel. A non-2xx response arrives as an HttpErrorResponse carrying this in `error`. */
interface ApiResponse<T> {
  timestamp: number;
  status: number;
  data?: T;
  errors: string[];
}

/**
 * DJI flight data for the current organization, and what can be done with it (ADN-2307). Every call is
 * organization-scoped on the Hub; a record, drone or flight that is not the caller's comes back as an error, not
 * as empty data. A failed request surfaces the Hub's own message (see `toDjiRequestError`) rather than a generic
 * HTTP one.
 */
@Injectable({
  providedIn: 'root'
})
export class DjiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /** DJI flights linked to a spray record, earliest first. Empty when none are linked. */
  async getSprayRecordFlights(sprayRecordId: string): Promise<DjiFlight[]> {
    return this.request(
      () => this.http.get<ApiResponse<DjiFlight[]>>(`${this.apiUrl}/dji/spray-records/${sprayRecordId}`),
      'Could not load DJI flight data for this record.'
    );
  }

  /** Whether ADN is receiving DJI flights for a drone. */
  async getDroneStatus(droneId: string): Promise<DjiDroneStatus> {
    return this.request(
      () => this.http.get<ApiResponse<DjiDroneStatus>>(`${this.apiUrl}/dji/drones/${droneId}`),
      'Could not load DJI status for this drone.'
    );
  }

  /**
   * What starting a spray record from this flight would create, without creating it. `alreadyLinked` carries
   * the record the flight already has, in which case there is nothing to preview.
   */
  async previewSprayRecordFromFlight(
    intakeId: string
  ): Promise<{ draft: DjiDraftPreview } | { alreadyLinked: string }> {
    try {
      const draft = await this.request(
        () => this.http.get<ApiResponse<DjiDraftPreview>>(`${this.apiUrl}/dji/flights/${intakeId}/spray-record-preview`),
        'Could not preview the spray record.'
      );
      return { draft };
    } catch (error) {
      if (error instanceof DjiRequestError && error.existingSprayRecordId) {
        return { alreadyLinked: error.existingSprayRecordId };
      }
      throw error;
    }
  }

  /**
   * Starts a draft spray record from a DJI flight and links the flight to it. Resolves with the record to open;
   * `alreadyLinked` is true when the flight already had one, which is returned instead of creating a second.
   */
  async createSprayRecordFromFlight(intakeId: string): Promise<{ sprayRecordId: string; alreadyLinked: boolean }> {
    try {
      const created = await this.request(
        () => this.http.post<ApiResponse<{ sprayRecordId: string }>>(`${this.apiUrl}/dji/flights/${intakeId}/create-spray-record`, {}),
        'Could not create the spray record.'
      );
      return { sprayRecordId: created.sprayRecordId, alreadyLinked: false };
    } catch (error) {
      if (error instanceof DjiRequestError && error.existingSprayRecordId) {
        return { sprayRecordId: error.existingSprayRecordId, alreadyLinked: true };
      }
      throw error;
    }
  }

  /** Sets a flight aside (a test or practice flight, not a real job). */
  async dismissFlight(intakeId: string): Promise<void> {
    await this.request(
      () => this.http.post<ApiResponse<unknown>>(`${this.apiUrl}/dji/flights/${intakeId}/dismiss`, {}),
      'Could not dismiss this flight.',
      false
    );
  }

  /** Takes a dismissal back. */
  async restoreFlight(intakeId: string): Promise<void> {
    await this.request(
      () => this.http.post<ApiResponse<unknown>>(`${this.apiUrl}/dji/flights/${intakeId}/restore`, {}),
      'Could not restore this flight.',
      false
    );
  }

  /**
   * Runs a request, returning the response's `data`. A failure becomes a `DjiRequestError` carrying the Hub's own
   * message. Pass `expectData = false` for a call whose success has nothing to return.
   */
  private async request<T>(call: () => Observable<ApiResponse<T>>, fallback: string, expectData = true): Promise<T> {
    try {
      const response = await firstValueFrom(call());
      if (expectData && response.data == null) {
        throw new Error(response.errors?.[0] || fallback);
      }
      return response.data as T;
    } catch (error) {
      throw toDjiRequestError(error, fallback);
    }
  }
}
